import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Mic, MicOff, Send, Volume2, VolumeX, RotateCcw, 
  Loader2, Zap, Flame, Laugh, ChevronDown, Play, Pause,
  User, Bot, Clock, AlertCircle
} from 'lucide-react';
import { API } from '../config/api';

const PRESETS = {
  clever_assassin: {
    name: "Clever Assassin",
    description: "Punchlines + wordplay",
    icon: Zap,
    color: "text-purple-400"
  },
  aggressive: {
    name: "Aggressive",
    description: "Hard-hitting bars",
    icon: Flame,
    color: "text-red-400"
  },
  comedy_roast: {
    name: "Comedy Roast", 
    description: "Funny + crowd friendly",
    icon: Laugh,
    color: "text-yellow-400"
  }
};

export default function VoiceBattlePage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [preset, setPreset] = useState('clever_assassin');
  const [turns, setTurns] = useState([]);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [waveformData, setWaveformData] = useState([]);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  
  // Audio playback
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioRef = useRef(null);
  
  const MAX_RECORDING_SECONDS = 20;

  // Initialize session on mount
  useEffect(() => {
    createSession();
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  const createSession = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const res = await fetch(`${API}/battlebot/session/create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ battle_mode: preset })
      });
      
      const data = await res.json();
      setSessionId(data.session_id);
      setTurns([]);
    } catch (error) {
      toast.error('Failed to create battle session');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create audio context for waveform visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Start waveform visualization
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateWaveform = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        setWaveformData([...dataArray].slice(0, 32));
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      };
      
      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      setWaveformData([]);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      
      updateWaveform();
      
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Could not access microphone. Please allow microphone permissions.');
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setIsRecording(false);
  }, []);

  const sendBattle = async () => {
    if (!audioBlob || !sessionId) return;
    
    setIsProcessing(true);
    setProcessingStep('Transcribing your bars...');
    
    try {
      // Step 1: Speech to Text
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('session_id', sessionId);
      
      const sttRes = await fetch(`${API}/battlebot/stt`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      
      const sttData = await sttRes.json();
      
      if (!sttData.success || !sttData.transcript) {
        toast.error('Could not transcribe audio. Please speak clearly and try again.');
        setIsProcessing(false);
        return;
      }
      
      const userTranscript = sttData.transcript;
      
      // Add user turn to UI immediately
      const userTurn = {
        type: 'user',
        text: userTranscript,
        timestamp: new Date().toISOString()
      };
      setTurns(prev => [...prev, userTurn]);
      
      // Step 2: Generate Bot Response
      setProcessingStep('Bot is writing bars...');
      
      const respondRes = await fetch(`${API}/battlebot/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          transcript: userTranscript,
          session_id: sessionId,
          tone_preset: preset
        })
      });
      
      const respondData = await respondRes.json();
      
      if (!respondData.success || !respondData.bot_response_text) {
        toast.error('Bot failed to respond. Try again!');
        setIsProcessing(false);
        return;
      }
      
      const botText = respondData.bot_response_text;
      
      // Step 3: Text to Speech
      setProcessingStep('Bot is spitting...');
      
      const ttsRes = await fetch(`${API}/battlebot/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          text: botText,
          voice_id: 'openai_onyx',
          session_id: sessionId
        })
      });
      
      const ttsData = await ttsRes.json();
      
      // Add bot turn
      const botTurn = {
        type: 'bot',
        text: botText,
        audio_url: ttsData.success ? ttsData.audio_url : null,
        moderation: respondData.moderation_status,
        timestamp: new Date().toISOString()
      };
      setTurns(prev => [...prev, botTurn]);
      
      // Auto-play bot response
      if (ttsData.success && ttsData.audio_url) {
        playAudio(ttsData.audio_url);
      }
      
      // Show moderation message if flagged
      if (respondData.moderation_status === 'flagged') {
        toast.info('Kept it clean for the platform.');
      }
      
      // Clear recording
      setAudioBlob(null);
      setRecordingTime(0);
      
    } catch (error) {
      console.error('Battle error:', error);
      toast.error('Battle failed. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    
    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => setIsPlaying(false);
    audio.onerror = () => {
      toast.error('Audio playback failed');
      setIsPlaying(false);
    };
    
    audio.play().catch(() => { setIsPlaying(false); });
    setCurrentAudio(audioUrl);
  };

  const togglePlayback = (audioUrl) => {
    if (currentAudio === audioUrl && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      playAudio(audioUrl);
    }
  };

  const resetBattle = async () => {
    if (sessionId) {
      try {
        await fetch(`${API}/battlebot/session/${sessionId}/reset`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
      } catch (e) {}
    }
    setTurns([]);
    setAudioBlob(null);
    setRecordingTime(0);
    createSession();
    toast.success('New battle started!');
  };

  return (
    <div className="min-h-screen py-8 px-4" data-testid="voice-battle-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase mb-2">
            <span className="text-secondary">Voice</span> Battle
          </h1>
          <p className="text-muted-foreground">Step up to the mic and battle the AI</p>
        </div>

        {/* Preset Selector */}
        <Card className="glass-card border-border p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium">Battle Style</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={resetBattle}
              className="text-muted-foreground"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              New Battle
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(PRESETS).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setPreset(key)}
                  className={`p-3 rounded-lg border transition-all ${
                    preset === key 
                      ? 'border-secondary bg-secondary/10' 
                      : 'border-border hover:border-border'
                  }`}
                  data-testid={`preset-${key}`}
                >
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${config.color}`} />
                  <p className="text-xs font-medium">{config.name}</p>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Battle Transcript */}
        <Card className="glass-card border-border p-4 mb-6 min-h-[300px] max-h-[400px] overflow-y-auto">
          {turns.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Bot className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Hit record and spit your bars.<br />
                The bot will respond with fire 🔥
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {turns.map((turn, idx) => (
                <div 
                  key={idx}
                  className={`flex gap-3 ${turn.type === 'user' ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    turn.type === 'user' ? 'bg-secondary/20' : 'bg-purple-500/20'
                  }`}>
                    {turn.type === 'user' ? (
                      <User className="w-4 h-4 text-secondary" />
                    ) : (
                      <Bot className="w-4 h-4 text-purple-400" />
                    )}
                  </div>
                  <div className={`flex-1 ${turn.type === 'user' ? 'text-left' : 'text-right'}`}>
                    <div className={`inline-block p-3 rounded-lg max-w-[85%] ${
                      turn.type === 'user' 
                        ? 'bg-secondary/10 border border-secondary/30' 
                        : 'bg-purple-500/10 border border-purple-500/30'
                    }`}>
                      <p className="text-sm whitespace-pre-line">{turn.text}</p>
                      {turn.audio_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePlayback(turn.audio_url)}
                          className="mt-2"
                        >
                          {currentAudio === turn.audio_url && isPlaying ? (
                            <><Pause className="w-4 h-4 mr-1" /> Pause</>
                          ) : (
                            <><Play className="w-4 h-4 mr-1" /> Play</>
                          )}
                        </Button>
                      )}
                      {turn.moderation === 'flagged' && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Kept clean
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {isProcessing && (
                <div className="flex gap-3 flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="flex-1 text-right">
                    <div className="inline-block p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">{processingStep}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Recording Controls */}
        <Card className="glass-card border-border p-6">
          {/* Waveform Visualization */}
          {isRecording && (
            <div className="flex items-center justify-center gap-1 h-12 mb-4">
              {waveformData.map((value, idx) => (
                <div
                  key={idx}
                  className="w-1 bg-secondary rounded-full transition-all duration-75"
                  style={{ height: `${Math.max(4, value / 4)}px` }}
                />
              ))}
            </div>
          )}
          
          {/* Recording Timer */}
          {(isRecording || audioBlob) && (
            <div className="text-center mb-4">
              <Badge variant="outline" className={isRecording ? 'animate-pulse' : ''}>
                <Clock className="w-3 h-3 mr-1" />
                {recordingTime}s / {MAX_RECORDING_SECONDS}s
              </Badge>
            </div>
          )}

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-4">
            {!audioBlob ? (
              <Button
                size="lg"
                variant={isRecording ? "destructive" : "default"}
                className={`rounded-full w-16 h-16 ${!isRecording ? 'bg-secondary hover:bg-secondary/90' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                data-testid="record-btn"
              >
                {isRecording ? (
                  <MicOff className="w-6 h-6" />
                ) : (
                  <Mic className="w-6 h-6" />
                )}
              </Button>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setAudioBlob(null);
                    setRecordingTime(0);
                  }}
                  disabled={isProcessing}
                >
                  Re-record
                </Button>
                <Button
                  className="bg-secondary hover:bg-secondary/90"
                  onClick={sendBattle}
                  disabled={isProcessing}
                  data-testid="send-btn"
                >
                  {isProcessing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send Bars</>
                  )}
                </Button>
              </div>
            )}
          </div>
          
          <p className="text-center text-xs text-muted-foreground mt-4">
            {isRecording ? 'Recording... Click to stop' : 'Tap to record your bars'}
          </p>
        </Card>

        {/* Tips */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          <p>💡 Speak clearly • Keep it under 20 seconds • Reference what the bot says</p>
        </div>
      </div>
    </div>
  );
}
